// frontend/src/components/dashboard/MetricsPanel.js
// Dashboard spec, page 6: Ticket summary, Effort/Utilization, FTR,
// On-time delivery and Add status split -- for every role, scoped
// server-side (manager tier: every engineer / "for all"; engineer: just
// themselves / "Individuals"). One Weekly/Monthly/Yearly toggle drives all
// of the period-filterable sections (Effort, FTR, On-time delivery).
import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, ToggleButton, ToggleButtonGroup, LinearProgress,
    Skeleton, Alert, Tooltip, Dialog, DialogTitle, DialogContent, Chip,
    TextField, MenuItem,
} from '@mui/material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

import { getMetrics, getTickets } from '../../services/endpoints';
import { useAuth } from '../../context/AuthContext';
import {
    STATUS_COLORS, statusChipColor, priorityChipColor, fmtDate, isManagerRole, TASK_TYPES,
} from '../../constants/domain';

// Last 12 months, newest first, as { value: 'YYYY-MM', label: 'October 2026' }.
const monthOptions = () => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        out.push({
            value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        });
    }
    return out;
};

const Panel = ({ title, subtitle, children, height = 320 }) => (
    <Paper sx={{ p: 2, height, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary" gutterBottom>{subtitle}</Typography>}
        <Box sx={{ flex: 1, minHeight: 0, mt: 1 }}>{children}</Box>
    </Paper>
);

const Empty = ({ message }) => (
    <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
        <Typography color="text.secondary">{message}</Typography>
    </Box>
);

// A row's statuses drive whether a cell can be drilled into: null means
// "every status" (the Total Parts row, no filter needed), one status is a
// clean filter, and 2+ (the Internal QC group row) is ambiguous -- skip it,
// its "- Peer QC" / "- Final QC" children cover that case individually.
const drilldownStatus = (r) => {
    if (!r.statuses) return { ok: true, status: null };
    if (r.statuses.length === 1) return { ok: true, status: r.statuses[0] };
    return { ok: false, status: null };
};

const TicketSummaryTable = ({ summary, onDrilldown }) => {
    const taskTypes = summary?.task_types || [];
    const rows = summary?.rows || [];
    if (rows.every(r => r.total === 0)) return <Empty message="No tickets yet." />;
    return (
        <TableContainer sx={{ height: '100%' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                        {taskTypes.map(t => (
                            <TableCell key={t} align="right" sx={{ fontWeight: 700 }}>{t}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(r => {
                        const { ok, status } = drilldownStatus(r);
                        const cellSx = (count, taskType) => ok && count > 0 ? {
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline', color: 'primary.main' },
                        } : {};
                        const click = (taskType) => {
                            if (!ok || r.total === 0) return;
                            onDrilldown({ label: r.label, status, taskType });
                        };
                        return (
                            <TableRow key={r.label} sx={{ opacity: r.kind === 'child' ? 0.75 : 1 }}>
                                <TableCell sx={{ pl: r.kind === 'child' ? 3 : 2, fontWeight: r.kind === 'total' ? 700 : 400 }}>
                                    {r.label}
                                </TableCell>
                                <TableCell align="right"
                                    onClick={() => click(null)}
                                    sx={{ fontWeight: r.kind === 'total' ? 700 : 400, ...cellSx(r.total) }}>
                                    {r.total}
                                </TableCell>
                                {taskTypes.map(t => (
                                    <TableCell key={t} align="right"
                                        onClick={() => click(t)}
                                        sx={cellSx(r[t] ?? 0, t)}>
                                        {r[t] ?? 0}
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const TicketDrilldownDialog = ({ open, onClose, title, loading, error, tickets }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Skeleton variant="rounded" height={200} />
            ) : tickets.length === 0 ? (
                <Empty message="Nothing matches this cell anymore." />
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ticket</TableCell>
                                <TableCell>Task type</TableCell>
                                <TableCell>Activity</TableCell>
                                <TableCell>Engineer</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Due date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tickets.map(t => (
                                <TableRow key={t.id} hover>
                                    <TableCell>{t.ticket_key}</TableCell>
                                    <TableCell>{t.task_type}</TableCell>
                                    <TableCell>{t.type_of_activity}</TableCell>
                                    <TableCell>{t.assigned_to_name || '-'}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={t.status} color={statusChipColor(t.status)} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={t.priority} color={priorityChipColor(t.priority)} />
                                    </TableCell>
                                    <TableCell>{fmtDate(t.due_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </DialogContent>
    </Dialog>
);

const EffortTable = ({ effort }) => {
    const rows = effort?.rows || [];
    if (rows.length === 0) return <Empty message="No one to report on yet." />;
    return (
        <TableContainer sx={{ height: '100%' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Engineer</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Estimated</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Actual</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>Utilization</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Remaining</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(r => (
                        <TableRow key={r.engineer_id}>
                            <TableCell>{r.engineer_name}</TableCell>
                            <TableCell align="right">{Number(r.estimated_hours).toFixed(1)}h</TableCell>
                            <TableCell align="right">{Number(r.actual_hours).toFixed(1)}h</TableCell>
                            <TableCell>
                                <Tooltip title={`${r.utilization_pct}% of ${effort.capacity_hours}h capacity`}>
                                    <LinearProgress variant="determinate"
                                        value={Math.min(100, r.utilization_pct)}
                                        color={r.utilization_pct > 100 ? 'error' : 'primary'}
                                        sx={{ height: 8, borderRadius: 4 }} />
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right">{Number(r.remaining_hours).toFixed(1)}h</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const FtrTable = ({ ftr }) => {
    const rows = ftr?.rows || [];
    if (rows.length === 0) return <Empty message="Nothing completed in this period yet." />;
    return (
        <TableContainer sx={{ height: '100%' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Engineer</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Completed</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>First-time-right</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>FTR %</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(r => (
                        <TableRow key={r.engineer_id}>
                            <TableCell>{r.engineer_name}</TableCell>
                            <TableCell align="right">{r.total_completed}</TableCell>
                            <TableCell align="right">{r.ftr_count}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {r.pct === null ? '-' : `${r.pct}%`}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const OnTimeChart = ({ onTime }) => {
    const series = onTime?.series || [];
    if (series.every(s => s.target === 0)) return <Empty message="Nothing due in this range yet." />;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <ReTooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="target" name="Target (due)" fill="#90a4ae" radius={[3, 3, 0, 0]} />
                <Bar dataKey="actual" name="On time" fill="#43a047" radius={[3, 3, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
};

const StatusSplitChart = ({ rows }) => {
    const pie = (rows || []).map(s => ({
        name: s.status, value: s.count, fill: STATUS_COLORS[s.status] || '#9e9e9e',
    }));
    if (pie.length === 0) return <Empty message="Nothing to show yet." />;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {pie.map((p, i) => <Cell key={i} fill={p.fill} />)}
                </Pie>
                <ReTooltip />
                <Legend verticalAlign="bottom" height={40} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
        </ResponsiveContainer>
    );
};

const MetricsPanel = () => {
    const { user } = useAuth();
    const manager = isManagerRole(user?.role);

    const [period, setPeriod] = useState('week');
    const [taskType, setTaskType] = useState('');
    const [month, setMonth] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [drilldown, setDrilldown] = useState(null); // { label, status, taskType }
    const [drilldownTickets, setDrilldownTickets] = useState([]);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [drilldownError, setDrilldownError] = useState(null);

    const load = useCallback(async (p, tt, mo) => {
        setLoading(true);
        try {
            const res = await getMetrics(p, { task_type: tt, month: mo });
            setData(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(period, taskType, month); }, [period, taskType, month, load]);

    const individual = (data?.effort?.rows?.length ?? 0) <= 1;

    const fTaskType = taskType;
    const openDrilldown = useCallback(async ({ label, status, taskType }) => {
        setDrilldown({ label, taskType });
        setDrilldownLoading(true);
        setDrilldownError(null);
        try {
            const filters = {};
            if (status) filters.status = status;
            if (taskType) filters.task_type = taskType;
            else if (fTaskType) filters.task_type = fTaskType;
            if (!manager) filters.assigned_to = user.id;
            const res = await getTickets(filters);
            setDrilldownTickets(res.data || []);
        } catch (err) {
            setDrilldownError(err.response?.data?.message || err.message);
        } finally {
            setDrilldownLoading(false);
        }
    }, [manager, user, fTaskType]);

    const closeDrilldown = () => setDrilldown(null);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                    {individual ? 'My Metrics' : 'Team Metrics'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField select size="small" label="Task type" value={taskType}
                    onChange={(e) => setTaskType(e.target.value)} sx={{ minWidth: 150 }}>
                    <MenuItem value=""><em>All task types</em></MenuItem>
                    {TASK_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Month" value={month}
                    onChange={(e) => setMonth(e.target.value)} sx={{ minWidth: 170 }}>
                    <MenuItem value=""><em>All months</em></MenuItem>
                    {monthOptions().map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
                <ToggleButtonGroup size="small" exclusive value={period}
                    onChange={(e, v) => v && setPeriod(v)}>
                    <ToggleButton value="week">Weekly</ToggleButton>
                    <ToggleButton value="month">Monthly</ToggleButton>
                    <ToggleButton value="year">Yearly</ToggleButton>
                </ToggleButtonGroup>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading && !data ? (
                <Grid container spacing={2}>
                    {[...Array(4)].map((_, i) => (
                        <Grid item xs={12} md={6} key={i}><Skeleton variant="rounded" height={320} /></Grid>
                    ))}
                </Grid>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Panel title="JIRA ticket status" subtitle="JIRA tickets by status. Filter by task type and month above.">
                            <TicketSummaryTable summary={data?.ticket_summary} onDrilldown={openDrilldown} />
                        </Panel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Panel title="Effort / Utilization"
                            subtitle={`Hours spent vs ${data?.effort?.capacity_hours ?? '-'}h ${period}ly capacity`}>
                            <EffortTable effort={data?.effort} />
                        </Panel>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Panel title="FTR (First Time Right)" subtitle={`Loop 0 at completion = first-time-right${month || taskType ? ' · filtered' : ''}`}>
                            <FtrTable ftr={data?.ftr} />
                        </Panel>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Panel title="On-time delivery" subtitle="Target (due) vs actual (delivered on time)">
                            <OnTimeChart onTime={data?.on_time_delivery} />
                        </Panel>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Panel title="Status split">
                            <StatusSplitChart rows={data?.status_split} />
                        </Panel>
                    </Grid>
                </Grid>
            )}

            <TicketDrilldownDialog
                open={!!drilldown}
                onClose={closeDrilldown}
                title={drilldown ? `${drilldown.label}${drilldown.taskType ? ` — ${drilldown.taskType}` : ''}` : ''}
                loading={drilldownLoading}
                error={drilldownError}
                tickets={drilldownTickets}
            />
        </Box>
    );
};

export default MetricsPanel;
